import {
  BitkitButton,
  BitkitLink,
  createBitkitToast,
  IconBlockCircle,
  IconCheck,
  IconClock,
  IconDot,
  IconTrigger,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card, type CardRootProps } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';

import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import ColorBar from '@/components/ColorBar/ColorBar';
import usePublicApiMutation from '@/hooks/api/usePublicApiMutation';
import { SimpleErrorResponse } from '@/models/Error';
import { getGitProviderIcon } from '@/models/GitProvider';
import { ManualApprovalBuild } from '@/pages/App/AppDetails/components/BuildsSection/ManualApprovalBuildList/hooks/useManualApprovalBuildList';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import { manualApprovalBuildPath } from '@/utils/publicApiRouteService';

type ActionButtonsProps = {
  slug: string;
  app: AppForAppPage;
  onRefresh: () => void;
};

const ActionButtons = ({ slug, app, onRefresh }: ActionButtonsProps) => {
  const { isMobile } = useResponsive();

  const { mutate: updateManualApprovalBuild, isPending: isUpdateManualApprovalBuildLoading } = usePublicApiMutation<
    null,
    SimpleErrorResponse,
    { isApproved: boolean }
  >(manualApprovalBuildPath(app.slug, slug), 'patch', {
    onSuccess: () => {
      onRefresh();
    },
    onError: (error) =>
      createBitkitToast({
        messageText: error?.response?.data?.message || 'Unknown error',
        titleText: 'Error',
        variant: 'critical',
      }),
  });

  return (
    <Box
      display="flex"
      flexDirection={['row-reverse', 'row']}
      flexWrap={['wrap', 'unset']}
      alignItems={['flex-start', 'center']}
      justifyContent="flex-end"
      gap="16"
      padding="12"
      width={isMobile ? '100%' : 'inherit'}
    >
      <BitkitButton
        variant="danger-tertiary"
        size="sm"
        icon={IconBlockCircle}
        onClick={() => updateManualApprovalBuild({ isApproved: false })}
        state={isUpdateManualApprovalBuildLoading ? 'loading' : undefined}
      >
        Decline
      </BitkitButton>
      <BitkitButton
        size="sm"
        icon={IconCheck}
        onClick={() => updateManualApprovalBuild({ isApproved: true })}
        state={isUpdateManualApprovalBuildLoading ? 'loading' : undefined}
      >
        {`Approve and run${isMobile ? '' : ' Build'}`}
      </BitkitButton>
    </Box>
  );
};

type ManualApprovalBuildListItemProps = {
  app: AppForAppPage;
  onRefresh: () => void;
  manualApprovalBuildItem: ManualApprovalBuild;
} & CardRootProps;

const ManualApprovalBuildListItem = ({
  app,
  onRefresh,
  manualApprovalBuildItem: { slug, buildParams, pullRequestUrl },
  ...rest
}: ManualApprovalBuildListItemProps) => {
  const { isMobile } = useResponsive();

  return (
    <Card.Root overflow="hidden" role="group" display="flex" {...rest}>
      <ColorBar color="color/blue/base" />
      <Box position="relative" flexGrow={1} flexShrink={1} minWidth="0">
        <Box
          display="flex"
          flexDirection={['column', 'row']}
          alignItems="center"
          justifyContent={['flex-end', 'center']}
          width="100%"
          borderBottom="1px solid"
          borderBottomColor="border/minimal"
        >
          <Box
            display="flex"
            flexDirection="row"
            gap="12"
            padding="12"
            alignItems="center"
            overflow="hidden"
            width="100%"
          >
            <IconClock size="24" color="color/blue/base" />
            <Box display="flex" flexDirection="column" overflow="hidden" flexGrow={1}>
              <Text truncate textStyle="sans/size-3/bold">
                PR is waiting for approval
              </Text>
              {!isMobile && (
                <Text truncate maxWidth="720px" textStyle="body/lg/regular" lineHeight="20px">
                  {buildParams.commitMessage}
                </Text>
              )}
            </Box>
          </Box>
          {isMobile && (
            <Box display="flex" width="100%" padding="12" borderTop="1px solid" borderTopColor="border/minimal">
              <Text truncate maxWidth="720px" textStyle="body/lg/regular" lineHeight="20px">
                {buildParams.commitMessage}
              </Text>
            </Box>
          )}
          {!isMobile && <ActionButtons slug={slug} app={app} onRefresh={onRefresh} />}
        </Box>
        <Box
          display="flex"
          justifyContent={['space-between', 'flex-start']}
          alignItems="center"
          gap="4"
          paddingX="12"
          paddingTop="8"
          paddingBottom="10px"
          borderBottom={isMobile ? '1px solid' : 'none'}
          borderBottomColor={isMobile ? 'border/minimal' : 'none'}
        >
          <BuildMetaData
            icon={IconTrigger}
            color="icon/secondary"
            textProps={{ color: 'text/secondary' }}
            lineHeight="20"
            flexShrink={0}
          >
            {buildParams.pullRequestAuthor}
          </BuildMetaData>
          <IconDot size="16" color="icon/tertiary" />
          <BuildMetaData
            icon={getGitProviderIcon(app.provider)}
            color="icon/interactive"
            textProps={{ color: 'text/link' }}
            flexShrink={0}
          >
            <BitkitLink
              colorVariant="purple"
              href={pullRequestUrl}
              textStyle="body/md/regular"
              whiteSpace="nowrap"
              isExternal
            >
              Review PR
            </BitkitLink>
          </BuildMetaData>
        </Box>
        {isMobile && <ActionButtons slug={slug} app={app} onRefresh={onRefresh} />}
      </Box>
    </Card.Root>
  );
};

export default ManualApprovalBuildListItem;
